import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Search, ClipboardList, CheckCircle, PhoneIncoming, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, LabelList } from 'recharts';
import { supabase } from '../../services/adminService';

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
};

const WFHTelecallerDetailReport = () => {
  const { telecallerId } = useParams();
  const [sp] = useSearchParams();

  const teamLeadId = sp.get('teamLeadId') || '';
  const batch = sp.get('batch') || '';
  const from = sp.get('from') || '';
  const to = sp.get('to') || '';
  const tcName = sp.get('name') || '';
  const tcEmail = sp.get('email') || '';

  const [q, setQ] = useState('');
  const [claimedGranularity, setClaimedGranularity] = useState('1h');

  const GRANULARITY_OPTIONS = [
    { key: '1d', label: '1d', bucketMinutes: 1440 },
    { key: '8h', label: '8h', bucketMinutes: 480 },
    { key: '4h', label: '4h', bucketMinutes: 240 },
    { key: '2h', label: '2h', bucketMinutes: 120 },
    { key: '1h', label: '1h', bucketMinutes: 60 },
    { key: '30min', label: '30m', bucketMinutes: 30 },
    { key: '15min', label: '15m', bucketMinutes: 15 },
    { key: '5min', label: '5m', bucketMinutes: 5 },
  ];

  const { data: rows = [], isLoading, error, refetch } = useQuery({
    queryKey: ['wfhTelecallerDetail', telecallerId, teamLeadId, batch, from, to],
    enabled: !!telecallerId && !!teamLeadId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_wfh_telecaller_activity', {
        p_team_lead_id: teamLeadId,
        p_telecaller_id: telecallerId,
        p_batch_name: batch || null,
        p_from_date: from || null,
        p_to_date: to || null,
      });
      if (error) throw error;
      return data || [];
    },
  });

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return (rows || []).filter((r) => {
      const hay = [
        r.name,
        r.contact_number,
        r.alternate_number,
        r.car_number,
        r.car_model,
        r.state,
        r.pin_code,
        r.status,
        r.disposition,
        r.disposition_notes,
        r.batch_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(qq);
    });
  }, [rows, q]);

  const summary = useMemo(() => {
    const out = { total: 0, disposed: 0, callback: 0, noDisp: 0, confirmed: 0 };
    (rows || []).forEach((r) => {
      out.total += 1;
      if (r.disposition) {
        out.disposed += 1;
        const d = String(r.disposition || '').trim().toLowerCase();
        if (d === 'callback') out.callback += 1;
        if (['confirmed', 'tl_confirmed', 'tl confirmed', 'tl-confirmed'].includes(d)) out.confirmed += 1;
      } else {
        out.noDisp += 1;
      }
    });
    return out;
  }, [rows]);

  const claimedByDateChart = useMemo(() => {
    const gran = GRANULARITY_OPTIONS.find((g) => g.key === claimedGranularity);
    const bucketMin = gran ? gran.bucketMinutes : 60;
    const buckets = new Map();

    (rows || []).forEach((r) => {
      if (!r.claimed_at) return;
      const d = new Date(r.claimed_at);
      if (Number.isNaN(d.getTime())) return;

      let key;
      if (bucketMin >= 1440) {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else {
        const totalMin = d.getHours() * 60 + d.getMinutes();
        const slotStart = Math.floor(totalMin / bucketMin) * bucketMin;
        const hh = String(Math.floor(slotStart / 60)).padStart(2, '0');
        const mm = String(slotStart % 60).padStart(2, '0');
        key = `${hh}:${mm}`;
      }
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });

    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([slot, count]) => ({
        date: bucketMin >= 1440 ? slot.slice(5) : slot,
        count,
      }));
  }, [rows, claimedGranularity]);

  const dispositionPieData = useMemo(() => {
    const buckets = new Map();
    (rows || []).forEach((r) => {
      const d = r.disposition ? String(r.disposition).trim() : 'No Disposition';
      buckets.set(d, (buckets.get(d) || 0) + 1);
    });
    return Array.from(buckets.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
  }, [rows]);

  const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

  const CustomPieLegend = ({ payload }) => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-3">
      {(payload || []).map((entry, i) => {
        const item = dispositionPieData[i];
        if (!item) return null;
        const pct = rows.length > 0 ? Math.round((item.value / rows.length) * 100) : 0;
        return (
          <div key={entry.value} className="flex items-center gap-1.5 text-xs text-gray-700">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
            <span className="font-medium">{item.name}</span>
            <span className="text-gray-400">({item.value} · {pct}%)</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to={`/wfh-employee-report?${new URLSearchParams({
                ...(teamLeadId ? { tl: teamLeadId } : {}),
                ...(batch ? { batch } : {}),
                ...(from ? { from } : {}),
                ...(to ? { to } : {}),
              }).toString()}`}
              className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
            >
              <ArrowLeft size={16} />
              Back to report
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">WFH Telecaller Detail</h1>
          <p className="text-gray-600 mt-1">
            <span className="font-medium text-gray-900">{tcName ? tcName : fmt(telecallerId)}</span>
            {tcEmail ? <span className="text-gray-500"> · {tcEmail}</span> : null}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Filters: TeamLead {teamLeadId}
            {batch ? ` · Batch ${batch}` : ' · All batches'}
            {from || to ? ` · Date ${from || '—'} → ${to || '—'}` : null}
          </p>
        </div>

        <button
          onClick={() => refetch()}
          className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          disabled={isLoading}
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Total Leads</span>
            <ClipboardList size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.total}</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Disposed</span>
            <CheckCircle size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.disposed}</div>
          {summary.total > 0 && (
            <div className="mt-1 text-xs opacity-80">{Math.round((summary.disposed / summary.total) * 100)}% of total</div>
          )}
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Confirmed</span>
            <CheckCircle size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.confirmed}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Callback</span>
            <PhoneIncoming size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.callback}</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">No Disposition</span>
            <Clock size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.noDisp}</div>
          {summary.total > 0 && (
            <div className="mt-1 text-xs opacity-80">{Math.round((summary.noDisp / summary.total) * 100)}% pending</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Claimed Leads</h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {GRANULARITY_OPTIONS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setClaimedGranularity(g.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  claimedGranularity === g.key
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="text-xs text-gray-500 mb-3">
          {rows.length} leads · selected date range
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8">No leads in selected date range.</div>
        ) : claimedByDateChart.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={claimedByDateChart}
              margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
              barCategoryGap={claimedByDateChart.length <= 3 ? '40%' : '20%'}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '13px' }}
                formatter={(value) => [`${value} leads`, 'Claimed']}
                cursor={{ fill: 'rgba(99,102,241,0.06)' }}
              />
              <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={60} fill="#6366f1">
                <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }} />
                {claimedByDateChart.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-sm text-gray-400 text-center py-8">No claimed_at timestamps available.</div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Disposition Breakdown</h3>
          <span className="text-xs text-gray-500">{rows.length} leads · selected date range</span>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8">No data in selected date range.</div>
        ) : dispositionPieData.length > 0 ? (
          <div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dispositionPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={55}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {dispositionPieData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '13px' }}
                  formatter={(value, name) => [`${value} leads`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <CustomPieLegend payload={dispositionPieData.map((d, i) => ({ value: d.name, color: CHART_COLORS[i % CHART_COLORS.length] }))} />
          </div>
        ) : (
          <div className="text-sm text-gray-400 text-center py-8">No dispositions yet.</div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="relative w-full max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name/phone/car/disposition..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="text-sm text-gray-600">{filtered.length} rows</div>
        </div>

        {!teamLeadId ? (
          <div className="text-gray-600">Missing Team Lead filter. Please open this page from WFH Employee Report.</div>
        ) : isLoading ? (
          <div className="text-gray-600">Loading...</div>
        ) : error ? (
          <div className="text-red-600">
            Failed to load detail report: {String(error?.message || error)}
            <div className="text-xs text-gray-500 mt-2">
              If you just added this feature, ensure SQL function <code>admin_wfh_telecaller_activity</code> is deployed.
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-600">No data found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disposition</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Claimed At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disposition At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={String(r.id)} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{fmt(r.name)}</div>
                      <div className="text-xs text-gray-500">
                        {fmt(r.car_model)} {r.car_number ? `· ${r.car_number}` : ''} {r.state ? `· ${r.state}` : ''}{' '}
                        {r.pin_code ? `· ${r.pin_code}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>{fmt(r.contact_number)}</div>
                      <div className="text-xs text-gray-500">{fmt(r.alternate_number)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmt(r.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="font-medium">{fmt(r.disposition)}</div>
                      <div className="text-xs text-gray-500">{fmt(r.disposition_notes)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmt(r.batch_name)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(r.claimed_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(r.disposition_updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WFHTelecallerDetailReport;

